import React, { useEffect, useRef, useState } from 'react'
import { ProgressBar } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import {
  type CrossClientActiveProgress,
  type CrossClientActiveTypeProgress,
  selectCrossClientActiveEvaluations,
  selectCrossClientResultsExecution,
  selectCrossClientActiveProgress,
} from '../../../../store/crossClient/crossClientResults'
import { useCancelCrossClientQueryEvaluationMutation } from '../../../../store/crossClient/crossClientExecution'
import { CrossClientQueryEvaluationStatuses, CrossClientQueryEvaluationTypes, type CrossClientQueryExecution } from '../../../../models/CrossClient'
import { getIntegrationSourceDisplayNames, pluralize } from '../../utils'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { CrossClientProgress } from '../../Progress/CrossClientProgress'
import './CrossClientProgressPopover.scss'

const AI_TITLE_PREFIX = 'Full review:'
const AI_TITLE_REST = 'gathering everything from meetings, notes and integrations'
const AI_STEP_NAME = 'full review'
const LOCAL_TITLE_PREFIX = 'Check integrations:'
const LOCAL_STEP_NAME = 'check integrations'

export const CrossClientProgressPopover: React.FC = () => {
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const progress = useAppSelector(selectCrossClientActiveProgress)

  if (!execution || progress.byType.length === 0) {
    return null
  }

  return <CrossClientProgressPopoverContent execution={execution} progress={progress} />
}

interface CrossClientProgressPopoverContentProps {
  execution: CrossClientQueryExecution
  progress: CrossClientActiveProgress
}

const CrossClientProgressPopoverContent: React.FC<CrossClientProgressPopoverContentProps> = ({ execution, progress }) => {
  const accountId = useAppSelector(selectAccountId)
  const activeEvaluations = useAppSelector(selectCrossClientActiveEvaluations)

  const [cancelEvaluation] = useCancelCrossClientQueryEvaluationMutation()
  const [open, setOpen] = useState(false)
  const [stoppedIds, setStoppedIds] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const allQueued = activeEvaluations.length > 0 && activeEvaluations.every((evaluation) => evaluation.status === CrossClientQueryEvaluationStatuses.QUEUED)

  const integrationNames = getIntegrationSourceDisplayNames(execution.sources)
  const localTitleRest = integrationNames.length > 0 ? `pulling records from ${integrationNames.join(' and ')}` : 'pulling records from your integrations'

  const stopType = (group: CrossClientActiveTypeProgress) => {
    group.evaluationIds.forEach((evaluationId) => {
      cancelEvaluation({ accountId, executionId: execution.id, evaluationId })
    })
    setStoppedIds((prev) => {
      const next = new Set(prev)
      group.evaluationIds.forEach((evaluationId) => next.add(evaluationId))
      return next
    })
  }

  return (
    <div className='cross-client-progress position-relative' ref={containerRef}>
      <button
        type='button'
        className='cross-client-progress-pill d-flex align-items-center gap-2 zocks-bg-color-white'
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {allQueued ? (
          <>
            <i className='bi bi-clock-history cross-client-progress-queued-icon' aria-hidden='true' />
            <span className='paragraph-small medium'>Waiting in queue</span>
          </>
        ) : (
          <>
            <CrossClientProgress total={progress.total} done={progress.processed} className='cross-client-progress-ring' lineWidth={40} />
            <span className='paragraph-small medium'>
              Checking {progress.processed} of {progress.total} clients
            </span>
          </>
        )}
        <i className='bi bi-chevron-expand cross-client-progress-chevron' aria-hidden='true' />
      </button>
      {open ? (
        <div className='cross-client-progress-panel zocks-bg-color-white'>
          <p className='paragraph-small zocks-text-gray-900 mb-3'>
            Results update as I go. Close this page if you need to. I&apos;ll send you an email when done.
          </p>
          <div className='d-flex flex-column gap-3'>
            {progress.byType.map((group) => {
              const isAi = group.type === CrossClientQueryEvaluationTypes.AI
              const stopping = group.evaluationIds.length > 0 && group.evaluationIds.every((evaluationId) => stoppedIds.has(evaluationId))
              return (
                <CrossClientProgressRow
                  key={group.type}
                  group={group}
                  titlePrefix={isAi ? AI_TITLE_PREFIX : LOCAL_TITLE_PREFIX}
                  titleRest={isAi ? AI_TITLE_REST : localTitleRest}
                  stepName={isAi ? AI_STEP_NAME : LOCAL_STEP_NAME}
                  stopping={stopping}
                  onStop={() => stopType(group)}
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

interface CrossClientProgressRowProps {
  group: CrossClientActiveTypeProgress
  titlePrefix: string
  titleRest: string
  stepName: string
  stopping: boolean
  onStop: () => void
}

const CrossClientProgressRow: React.FC<CrossClientProgressRowProps> = ({ group, titlePrefix, titleRest, stepName, stopping, onStop }) => {
  const percent = group.total > 0 ? Math.round((group.processed / group.total) * 100) : 0
  const isAi = group.type === CrossClientQueryEvaluationTypes.AI

  const extras: string[] = []
  if (group.newMatches > 0) {
    extras.push(`${group.newMatches} ${pluralize(group.newMatches, 'match', 'matches')}`)
  }
  if (isAi) {
    if (group.missingData > 0) {
      extras.push(`${group.missingData} missing data`)
    }
  } else if (group.stillPotential > 0) {
    extras.push(`${group.stillPotential} potential`)
  }

  return (
    <div className='d-flex flex-column gap-1'>
      <div className='d-flex align-items-start justify-content-between gap-2'>
        <span className='paragraph-small zocks-text-gray-900'>
          <span className='fw-600'>{titlePrefix}</span> {titleRest}
        </span>
        <Tooltip placement='top' title={stopping ? `Canceling ${stepName}…` : `Stop ${stepName}. You can resume this at any time.`}>
          <button
            type='button'
            className='cross-client-progress-stop'
            onClick={onStop}
            disabled={stopping}
            aria-disabled={stopping}
            aria-label={stopping ? `Canceling ${stepName}` : `Stop ${stepName}`}
          >
            {stopping ? <Spinner size='sm' /> : <i className='bi bi-stop-fill' aria-hidden='true' />}
          </button>
        </Tooltip>
      </div>
      <ProgressBar now={percent} className='cross-client-progress-bar' />
      <span className='paragraph-x-small'>
        <span className='zocks-text-gray-900'>
          {group.processed} of {group.total} clients
        </span>
        {extras.length ? <span className='zocks-text-gray-600'> · {extras.join(' · ')}</span> : null}
      </span>
    </div>
  )
}
